import { Link, useSearchParams } from 'react-router-dom';

import { formatCredits, formatInt, formatUsd } from '@/lib/reportsChart';
import { canReadUserWorkflows } from '@/lib/roles';
import { useMe } from '@/lib/useMe';
import { useWorkflowList, type WorkflowSort } from '@/lib/workflowsApi';

import { DebouncedInput } from './reports/DebouncedInput';
import { ScrollFade } from './reports/ScrollFade';

const PAGE_SIZE = 25;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * User workflows: every workflow of every user with what it has cost, newest
 * spend first by default. Opening one (/workflows/:id) is read-only and
 * audited. Search, sort and page live in the URL.
 */
export function WorkflowsPage() {
  const { data: me } = useMe();
  const allowed = canReadUserWorkflows(me?.role, me?.capabilities);
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const sort: WorkflowSort = params.get('sort') === 'recent' ? 'recent' : 'spend';
  const page = Math.max(1, Number(params.get('page')) || 1);

  function update(next: { q?: string; sort?: WorkflowSort; page?: number }) {
    const merged = { q, sort, page, ...next };
    const out = new URLSearchParams();
    if (merged.q) out.set('q', merged.q);
    if (merged.sort !== 'spend') out.set('sort', merged.sort);
    if (merged.page > 1) out.set('page', String(merged.page));
    setParams(out, { replace: true });
  }

  const { data, isLoading, isError } = useWorkflowList(
    { q, sort, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    allowed,
  );

  if (!allowed) {
    return (
      <div>
        <h1 className="text-card-title font-semibold text-fg-primary">User workflows</h1>
        <p className="mt-2 text-body text-fg-secondary">
          Viewing users&apos; workflows is for the superadmin role only.
        </p>
      </div>
    );
  }

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <h1 className="text-card-title font-semibold text-fg-primary">User workflows</h1>
      <p className="mt-1 text-body text-fg-secondary">
        Open anyone&apos;s workflow read-only. Every view is audited. Credits are what the user was
        charged; USD is the provider cost (both net).
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <DebouncedInput
          label="Search"
          value={q}
          onChange={(next) => update({ q: next, page: 1 })}
          placeholder="email, name or workflow"
          className="w-72 max-w-full"
        />
        <div className="flex gap-2" role="group" aria-label="Sort">
          {(
            [
              ['spend', 'Most spend'],
              ['recent', 'Most recent'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={sort === value}
              onClick={() => update({ sort: value, page: 1 })}
              className={`rounded-card border px-3 py-1 text-meta ${
                sort === value
                  ? 'border-primary bg-surface-brand-subtle text-fg-primary'
                  : 'border-border text-fg-secondary hover:bg-state-hover'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="mt-4 text-body text-fg-hint">Loading…</p>}
      {isError && (
        <p className="mt-4 text-body text-destructive">Could not load workflows. Try again.</p>
      )}
      {data && data.items.length === 0 && (
        <p className="mt-4 text-body text-fg-hint">No workflows match.</p>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="mt-4">
            <ScrollFade pinnedEnd>
            <table className="w-full text-left text-body" data-testid="workflows-table">
              <thead>
                <tr className="border-b border-border bg-surface-muted text-meta text-fg-secondary">
                  <th className="sticky left-0 z-10 bg-surface-muted px-3 py-2">User</th>
                  <th className="px-3 py-2">Workflow</th>
                  <th className="px-3 py-2 text-right">Runs</th>
                  <th className="px-3 py-2">Last run</th>
                  <th className="px-3 py-2 text-right">Generations (media), credits / USD</th>
                  <th className="px-3 py-2 text-right">LLM (agent chat + text nodes), credits / USD</th>
                  <th className="sticky right-0 z-10 border-l border-border bg-surface-muted px-3 py-2 text-right">Total, credits / USD</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((w) => (
                  <tr key={w.pipeline_id} className="border-b border-border last:border-0">
                    <td className="sticky left-0 z-10 max-w-[10rem] bg-background px-3 py-2 sm:max-w-none">
                      <div className="truncate text-fg-primary">{w.email ?? w.user_name ?? w.profile_id ?? 'System template'}</div>
                      {w.email && w.user_name && (
                        <div className="text-meta text-fg-hint">{w.user_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/workflows/${w.pipeline_id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {w.name || 'Untitled workflow'}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatInt(w.runs)}</td>
                    <td className="px-3 py-2 text-fg-secondary">{formatDate(w.last_run_at)}</td>
                    <Money credits={w.generation_credits} usd={w.generation_usd} />
                    <Money credits={w.llm_credits} usd={w.llm_usd} />
                    <Money credits={w.total_credits} usd={w.total_usd} strong />
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollFade>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => update({ page: page - 1 })}
              className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-meta text-fg-secondary">
              Page {page} of {pages} · {formatInt(total)} workflows
            </span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => update({ page: page + 1 })}
              className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Money({ credits, usd, strong }: { credits: number; usd: number; strong?: boolean }) {
  return (
    <td
      className={`px-3 py-2 text-right tabular-nums ${
        strong ? 'sticky right-0 z-10 border-l border-border bg-background font-semibold text-fg-primary' : ''
      }`}
    >
      {formatCredits(credits)} / {formatUsd(usd)}
    </td>
  );
}
